import { BaseSqliteRepository } from './base-sqlite.repository';
import { SqliteQueryError } from '../../errors';
import type { ICreditorPaymentRepository } from '@app/domain/ports';
import type { CreditorPayment } from '@app/domain/entities';

interface CreditorPaymentRow {
  id: number;
  creditorId: number;
  date: number; // Unix timestamp in seconds
  accountId: number;
  accountName: string;
  currencyCode: string;
  amount: number;
  exchangeRate: number;
  note: string;
}

export class CreditorPaymentRepository extends BaseSqliteRepository implements ICreditorPaymentRepository {
  private readonly BASE_FROM = `
    FROM creditor_payments cp
    INNER JOIN accounts a ON cp.account_id = a._id
    INNER JOIN currencies c ON cp.currency_id = c._id
    INNER JOIN available_currencies AS cl ON c.available_currency_id = cl._id
    WHERE cp.is_deleted = 0
  `;

  private readonly BASE_FIELDS = [
    'cp._id as id',
    'cp.creditor_id as creditorId',
    'cp.payment_date as date',
    'cp.account_id as accountId',
    'a.name as accountName',
    'cl.ticker as currencyCode',
    'cp.amount as amount',
    'cp.exchange_rate as exchangeRate',
    'cp.note as note',
  ];

  async findByCreditorId(creditorId: number): Promise<CreditorPayment[]> {
    const query = `
      SELECT
        ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
        AND cp.creditor_id = ?
      ORDER BY cp.payment_date DESC, cp._id DESC`;

    const rows = await this.query<CreditorPaymentRow>(query, [creditorId]);
    return rows.map((row) => this.mapRowToEntity(row));
  }

  async insert(payment: Omit<CreditorPayment, 'id' | 'accountName'>): Promise<number> {
    const date = new Date(payment.date).setUTCHours(0, 0, 0, 0) / 1000;

    const result = await this.queryOne<{ id: number }>(
      `INSERT INTO creditor_payments (creditor_id, payment_date, account_id, amount, currency_id, exchange_rate, note)
       VALUES (?, ?, ?, ?, (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?), ?, ?)
       RETURNING _id AS id`,
      [
        payment.creditorId,
        date,
        payment.accountId,
        payment.amount,
        payment.currencyCode,
        payment.exchangeRate,
        payment.note,
      ],
    );

    if (!result?.id) {
      throw new SqliteQueryError('INSERT INTO creditor_payments', [], 'Failed to insert creditor payment');
    }

    return result.id;
  }

  async update(payment: CreditorPayment): Promise<void> {
    const date = new Date(payment.date).setUTCHours(0, 0, 0, 0) / 1000;

    await this.execute(
      `UPDATE creditor_payments
       SET payment_date = ?,
           account_id = ?,
           amount = ?,
           currency_id = (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?),
           exchange_rate = ?,
           note = ?,
           updated_at = strftime('%s','now')
       WHERE _id = ?`,
      [date, payment.accountId, payment.amount, payment.currencyCode, payment.exchangeRate, payment.note, payment.id],
    );
  }

  async delete(id: number): Promise<void> {
    await this.execute("UPDATE creditor_payments SET is_deleted = 1, updated_at = strftime('%s','now') WHERE _id = ?", [
      id,
    ]);
  }

  async countByAccount(accountId: number): Promise<number> {
    const result = await this.queryOne<{ count: number }>(
      `SELECT COUNT(*) as count FROM creditor_payments WHERE account_id = ? AND is_deleted = 0`,
      [accountId],
    );
    return result?.count ?? 0;
  }

  async bulkUpdateAccount(sourceAccountId: number, targetAccountId: number): Promise<void> {
    await this.execute(
      `UPDATE creditor_payments
       SET account_id = ?,
           updated_at = strftime('%s','now')
       WHERE account_id = ? AND is_deleted = 0`,
      [targetAccountId, sourceAccountId],
    );
  }

  private mapRowToEntity(row: CreditorPaymentRow): CreditorPayment {
    return {
      id: row.id,
      creditorId: row.creditorId,
      date: new Date(row.date * 1000),
      accountId: row.accountId,
      accountName: row.accountName,
      currencyCode: row.currencyCode,
      amount: row.amount,
      exchangeRate: !row.exchangeRate ? 1 : row.exchangeRate,
      note: row.note,
    };
  }
}
